import { useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { ToggleView } from "../components/Pages/Diary/SubHeader/SubHeader";

export type UrlParams = {
  date?: string;
  venueId?: string;
  tab?: string;
  bookingId?: string;
};

export type UrlRouteParams = {
  venueId?: string;
  tab?: string;
  bookingId?: string;
};

export const useUrlSearchParams = () => {
  const urlParamSearch = new URLSearchParams(useLocation().search);
  const urlParams: UrlParams = {};
  const allowedKeys: Array<keyof UrlParams> = [
    "date",
    "venueId",
    "tab",
    "bookingId"
  ];
  urlParamSearch.forEach((value: string, key: string) => {
    if (allowedKeys.includes(key as keyof UrlParams)) {
      urlParams[key as keyof UrlParams] = value;
    }
  });

  return urlParams;
};

export const useUrlNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getToggleViewFromTab = useCallback((tab?: string): ToggleView => {
    if (tab === "run-sheet") return "RunsheetTab";
    if (tab === "floorplan") return "FloorplanTab";
    return "DiaryTab";
  }, []);

  const updateUrl = useCallback(
    (params: {
      venueId?: string;
      tab?: ToggleView;
      date?: Date;
      bookingId?: string;
    }) => {
      const { venueId, tab, date, bookingId } = params;

      if (bookingId) {
        const path = `/bookings/${bookingId}`;
        if (location.pathname !== path) {
          navigate(path);
        }
        return;
      }

      if (!venueId || !tab) {
        return;
      }

      const dateStr = date ? date.toISOString().split("T")[0] : undefined;
      let tabPath = "";
      switch (tab) {
        case "DiaryTab":
          tabPath = "diary";
          break;
        case "RunsheetTab":
          tabPath = "run-sheet";
          break;
        case "FloorplanTab":
          tabPath = "floorplan";
          break;
        default:
          tabPath = "diary";
      }

      const path = `/sites/${venueId}/${tabPath}`;
      const searchQuery = dateStr ? `?date=${dateStr}` : "";
      const fullPath = `${path}${searchQuery}`;

      const currentFullPath = location.pathname + location.search;

      if (currentFullPath !== fullPath) {
        navigate(fullPath);
      }
    },
    [navigate, location.pathname, location.search]
  );

  return {
    updateUrl,
    getToggleViewFromTab
  };
};
